import React, { useEffect, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'
import {
  capitalize,
  formatMediumDate,
  stripHtml,
} from '@/utilities'
import { Box, Button, Icon, Link, ListGrid, ListGridItem, Text } from '@maui/elements'
import withFreemium from '../common/Freemium/WithFreemium'
import { Details, Term, Definition } from '../common/Details'
import { closeModal, openModal } from '../../events'
import RelevantReportsDetail from './RelevantReportsDetail'
import { ExploitsDetail } from './ExploitsDetail'
import { normalizeCve, normalizeCveReports } from '../../api/normalizers'
import { useFetch } from '../../hooks/useFetch'
import Markdown from 'markdown-to-jsx'

const cveColorTheme = {
  cve: {
    default: {
      color: 'colorRiskBenign'
    },
    low: {
      color: 'colorRiskLow'
    },
    medium: {
      color: 'colorRiskMedium'
    },
    high: {
      color: 'colorRiskHigh'
    },
    critical: {
      color: 'colorRiskCritical'
    },
    none: {
      color: 'colorRiskBenign'
    },
    easy: {
      color: 'colorRiskLow'
    },
    moderate: {
      color: 'colorRiskMedium'
    },
    difficult: {
      color: 'colorRiskHigh'
    },
  }
}

const RatingValue = ({ children, color }) => {
  return (
    <Box fontWeight='500' fontSize='24px' lineHeight='32px' color={color}>
      {children}
    </Box>
  )
}

RatingValue.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string
}

const RatingValueWithFreemium = withFreemium({ prop: 'value', layout: 'inline' })(({ color, value }) =>
  <RatingValue color={color}>
    {capitalize((Array.isArray(value) ? value[0] : value) || '')}
  </RatingValue>
)

const YesOrNoWithFreemium = withFreemium({ prop: 'value', layout: 'inline' })(({ color, value }) =>
  <RatingValue color={color}>
    {value ? 'Yes' : 'No'}
  </RatingValue>
)

const noDataAvailable = 'No data available'

const SummaryWithFreemium = withFreemium({ prop: 'summary', layout: 'fullCenter' })(({ summary }) => <ExecutiveSummary summary={summary} />)
const DescriptionWithFreemium = withFreemium({ prop: 'description', layout: 'fullCenter' })(({ description }) => stripHtml(description))
const AnalysisWithFreemium = withFreemium({ prop: 'analysis', layout: 'fullCenter' })(({ analysis }) => <Analysis analysis={analysis} />)
const DisclosureDateWithFreemium = withFreemium({ prop: 'disclosureDate', layout: 'fullCenter' })(({ disclosureDate }) => disclosureDate)
const VulnTypeWithFreemium = withFreemium({ prop: 'vulnType', layout: 'fullCenter' })(({ vulnType }) => vulnType)
const VulnProductsWithFreemium = withFreemium({ prop: 'vulnProducts', layout: 'fullCenter' })(({ vulnProducts }) => <VulnProducts vulnProducts={vulnProducts}/>)
const VenderDetailsWithFreemium = withFreemium({ prop: 'vendorFixDetails', layout: 'fullCenter' })(({ vendorFixDetails }) => <VendorDetails vendorFixDetails={vendorFixDetails} />)
const ExploitConsequenceWithFreemium = withFreemium({ prop: 'exploitConsequence', layout: 'fullCenter' })(({ exploitConsequence }) => exploitConsequence)
const MitigationWithFreemium = withFreemium({ prop: 'mitigation', layout: 'fullCenter' })(({ mitigation }) => mitigation.join(', '))
const WorkaroundsWithFreemium = withFreemium({ prop: 'workarounds', layout: 'fullCenter' })(({ workarounds }) => stripHtml(workarounds))
const SourcesWithFreemium = withFreemium({ prop: 'sources', layout: 'fullCenter' })(({ sources }) => <Sources sources={sources} />)
const ActorsWithFreemium = withFreemium({ prop: 'actors', layout: 'fullCenter' })(({ actors }) => {
  return (
    <ListGrid list={actors} type='ActorModal' cellWidth={['100%', '50%', '20%']}>
      {item => (
        <ListGrid.Item onClick={() => onActorClick(item)}>
          <Link display='flex' gap='8px'>
            <Icon name='user-secret' />
            {item.name}
          </Link>
        </ListGrid.Item>
      )}
    </ListGrid>
  )
})
const MalwareWithFreemium = withFreemium({ prop: 'malware', layout: 'fullCenter' })(({ malware }) => {
  return (
    <ListGrid list={malware} type='MalwareModal' cellWidth={['100%', '50%', '20%']}>
      {item => (
        <ListGrid.Item onClick={() => onMalwareClick(item)}>
          <Link display='flex' gap='8px'>
            <Icon name='user-secret' />
            {item.name}
          </Link>
        </ListGrid.Item>
      )}
    </ListGrid>
  )
})

const onActorClick = (actor) => {
  closeModal()
  openModal('ActorModal', actor.id, actor.name)
}

const onMalwareClick = (malware) => {
  closeModal()
  openModal('MalwareModal', malware.id, malware.name)
}

// Severity
const SeveritySummary = ({ exploitRating, risk, inTheWild, zeroDay, attackEase }) => {
  const theme = cveColorTheme
  const zeroDayColor = zeroDay ? theme.cve.critical.color : theme.cve.default.color
  const inWildColor = inTheWild ? theme.cve.critical.color : theme.cve.default.color
  const attackColor = attackEase ? theme.cve[attackEase?.toLowerCase()].color : theme.cve.default.color

  const riskKey = risk?.toLowerCase()

  return (
    <Box display="flex" flexWrap="wrap" gap="8">
      {!isEmpty(exploitRating) && (
        <Box flexBasis="22%">
          <Text color="colorTextDeemphasis" mb="spaceGapMedium">Exploitation State</Text>
          <RatingValueWithFreemium
            color={theme.cve[exploitRating]?.color || theme.cve.default.color}
            value={exploitRating}
          />
        </Box>
      )}
      {!isEmpty(exploitRating) && (
        <Box flexBasis="22%">
          <Text color="colorTextDeemphasis" mb="spaceGapMedium">Risk Rating</Text>
          <RatingValueWithFreemium
            color={theme.cve[riskKey]?.color || theme.cve.default.color}
            value={riskKey}
          />
        </Box>
      )}
      <Box flexBasis="22%">
        <Text color="colorTextDeemphasis" mb="spaceGapMedium">Exploited in the Wild</Text>
        <YesOrNoWithFreemium
          color={inWildColor}
          value={inTheWild}
        />
      </Box>
      <Box flexBasis="22%">
        <Text color="colorTextDeemphasis" mb="spaceGapMedium">Exploited as Zero-Day</Text>
        <YesOrNoWithFreemium
          color={zeroDayColor}
          value={zeroDay}
        />
      </Box>
      {!!attackEase?.length && (
        <Box flexBasis="22%">
          <Text color="colorTextDeemphasis" mb="spaceGapMedium">Ease of Attack</Text>
          <RatingValueWithFreemium
            color={attackColor}
            value={attackEase}
          />
        </Box>
      )}
    </Box>
  )
}

SeveritySummary.propTypes = {
  attackEase: PropTypes.shape({
    length: PropTypes.any,
    toLowerCase: PropTypes.func
  }),
  exploitRating: PropTypes.string,
  inTheWild: PropTypes.string,
  risk: PropTypes.shape({
    toLowerCase: PropTypes.func
  }),
  zeroDay: PropTypes.string
}

// Vendor Details
const VendorDetails = ({ vendorFixDetails }) => {
  if (!vendorFixDetails || !vendorFixDetails.length || !Array.isArray(vendorFixDetails)) return noDataAvailable
  return vendorFixDetails.map((detail, i) => {
    return (
      <Box key={i} marginBottom="spaceGapMedium">
        <Icon name='notepad' marginRight="spaceGapMedium" />
        <Box
          as="a"
          color="colorTextMain"
          href={detail.url}
          target='_blank'
        >
          {detail.name}
        </Box>
      </Box>
    )
  })
}

// Executive Summary
const ExecutiveSummary = ({ summary }) => {
  return (
    <Markdown
      options={{
        overrides: {
          p: {
            props: { style: { margin: '0px' } }
          }
        }
      }}
    >
      {summary}
    </Markdown>
  )
}

ExecutiveSummary.propTypes = {
  summary: PropTypes.string
}

// Vuln Products
const VulnProducts = ({ vulnProducts }) => {
  return (
    <Markdown
      options={{
        overrides: {
          p: {
            props: { style: { margin: '0px' } }
          }
        }
      }}
    >
      {vulnProducts}
    </Markdown>
  )
}

VulnProducts.propTypes = {
  vulnProducts: PropTypes.string
}

// Analysis
const Analysis = ({ analysis }) => {
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [showExpander, setShowExpander] = useState(false)
  const ref = useRef()

  // If the content is not at least 10 lines hide the expander
  useEffect(() => {
    setShowExpander(ref?.current?.scrollHeight > ref?.current?.clientHeight)
  }, [ref])

  const style = !showAnalysis && analysis
    ? {
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 10,
        display: '-webkit-box',
        overflow: 'hidden',
      }
    : {}

  return (
    <Box>
      <Box ref={ref} style={style}>
        <Markdown
          options={{
            overrides: {
              p: {
                props: { style: { margin: '0px' } }
              },
              ul: {
                props: { style: { margin: '10px 0 0 0' } }
              },
              em: {
                props: { style: { display: 'flex' } }
              },
              a: {
                props: { style: { display: 'inline-flex' } }
              }
            }
          }}
        >
          {analysis}
        </Markdown>

      </Box>
      { showExpander && showAnalysis &&
        <Button
          paddingLeft="0"
          variant="primaryText"
          onClick={() => setShowAnalysis(false)}
        >
          Show less...
        </Button>
      }
      { showExpander && !showAnalysis &&
        <Button
          paddingLeft="0"
          variant="primaryText"
          onClick={() => setShowAnalysis(true)}
        >
          Show more...
        </Button>
      }
    </Box>
  )
}

Analysis.propTypes = {
  analysis: PropTypes.string
}

// Sources
const Sources = ({ sources }) => {
  return sources.map((source, i) => (
    <Link
      key={i}
      color='colorActivePrimary'
      href={source.url}
      alt="Open full details"
      target="_blank"
      rel="noopener noreferrer"
      title={source.source_description}
      display="flex"
      alignItems="center"
      gap="8px"
      marginBottom="4px"
    >
      <Icon name="external-link" />
      <Text>{source.source_name}</Text>
    </Link>
  ))
}
export function CveDetails({ id, cveData }) {
  const { data: cveReportsData } = useFetch(`/api/v4/vulnerability/${id}`)

  const cve = useMemo(() => {
    if (!cveData) return null
    return normalizeCve(cveData)
  }, [cveData])

  const cveReports = useMemo(() => {
    if (!cveReportsData) return null
    return normalizeCveReports(cveReportsData?.associated_reports)
  }, [cveReportsData])

  if (!cve) return null

  const {
    analysis,
    associated_actors: associatedActors = [],
    associated_malware: associatedMalware = [],
    available_mitigation: mitigation,
    date_of_disclosure: disclosureDate,
    description,
    ease_of_attack: attackEase,
    exploitation_consequence: exploitConsequence,
    exploitation_state: exploitRating,
    exploitation_vectors: exploitVectors,
    exploits,
    executive_summary: summary,
    mitigation_notes: mitigationDetails,
    observed_in_the_wild: inTheWild,
    risk_rating: risk,
    sources,
    vendor_fix_references: vendorFixDetails = [],
    cwe: vulnType,
    vulnerable_products: vulnProducts,
    was_zero_day: zeroDay,
    workarounds,
  } = cve || {}

  return (
    <Details>
      { !isEmpty(summary) && (
        <>
          <Term>Executive Summary</Term>
          <Definition>
            <SummaryWithFreemium summary={summary} />
          </Definition>
        </>
      )}

      <Term>Severity</Term>
      <Definition>
        <SeveritySummary
          exploitRating={exploitRating}
          risk={risk}
          inTheWild={inTheWild}
          zeroDay={zeroDay}
          attackEase={attackEase}
        />
      </Definition>

      { !isEmpty(vulnType) && (
        <>
          <Term>CWE</Term>
          <Definition>
            <VulnTypeWithFreemium vulnType={vulnType} />
          </Definition>
        </>
      )}

      { !isEmpty(mitigation) && (
        <>
          <Term>Mitigation</Term>
          <Definition>
            <MitigationWithFreemium mitigation={mitigation} />
          </Definition>
        </>
      )}

      { !isEmpty(disclosureDate) && (
        <>
          <Term>Date of Disclosure</Term>
          <Definition>
            <DisclosureDateWithFreemium disclosureDate={formatMediumDate(disclosureDate)} />
          </Definition>
        </>
      )}

      { !isEmpty(exploitConsequence) && (
        <>
          <Term>Exploitation Consequence</Term>
          <Definition>
            <ExploitConsequenceWithFreemium exploitConsequence={exploitConsequence} />
          </Definition>
        </>
      )}

      { !isEmpty(exploitVectors) && (
        <>
          <Term>Exploitation Vectors</Term>
          <Definition>
            <ListGrid list={exploitVectors} cellWidth={['100%', '50%', '20%']}>
              {item => <ListGridItem>{item}</ListGridItem>}
            </ListGrid>
          </Definition>
        </>
      )}

      { !isEmpty(description) && (
        <>
          <Term>Description</Term>
          <Definition>
            <DescriptionWithFreemium description={description} />
          </Definition>
        </>
      )}

      { !isEmpty(analysis) && (
        <>
          <Term>Analysis</Term>
          <Definition>
            <AnalysisWithFreemium analysis={analysis} />
          </Definition>
        </>
      )}

      { !isEmpty(vulnProducts) && (
        <>
          <Term>Vulnerable Products</Term>
          <Definition>
            <VulnProductsWithFreemium vulnProducts={vulnProducts} />
          </Definition>
        </>
      )}

      { !isEmpty(vendorFixDetails) && (
        <>
          <Term>Vendor Fix Details</Term>
          <Definition>
            <VenderDetailsWithFreemium vendorFixDetails={vendorFixDetails} />
          </Definition>
        </>
      )}

      { !isEmpty(workarounds) && (
        <>
          <Term>Workarounds</Term>
          <Definition>
            <WorkaroundsWithFreemium workarounds={workarounds} />
          </Definition>
        </>
      )}

      { !isEmpty(mitigationDetails) && (
        <>
          <Term>Mitigation</Term>
          <Definition>
            <MitigationWithFreemium mitigation={mitigationDetails} />
          </Definition>
        </>
      )}

      { !isEmpty(associatedActors) && (
        <>
          <Term>Associated Actors</Term>
          <Definition>
            <ActorsWithFreemium actors={associatedActors} />
          </Definition>
        </>
      )}

      { !isEmpty(associatedMalware) && (
        <>
          <Term>Associated Malware</Term>
          <Definition>
            <MalwareWithFreemium malware={associatedMalware} />
          </Definition>
        </>
      )}

      { !isEmpty(exploits) && (
        <ExploitsDetail list={exploits}/>
      )}

      { !isEmpty(sources) && (
        <>
          <Term>Sources</Term>
          <Definition>
            <SourcesWithFreemium sources={sources} />
          </Definition>
        </>
      )}

      { Array.isArray(cveReports) && cveReports.length > 0 && (
        <RelevantReportsDetail list={cveReports} blueIcon='sheild-alert' type="CVE" />
      )}
    </Details>
  )
}

CveDetails.propTypes = {
  id: PropTypes.string,
  cveData: PropTypes.object,
}

export default CveDetails
